import { Badge, Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import { PageHeader } from './_shared';
import {
  AGING_RAMP,
  AGING_TOTALS,
  AP_OVERDUE,
  AP_TOTAL,
  AS_OF_LABEL,
  SUPPLIER_INVOICES,
  peso,
  pesoShort,
} from './sample-data';

export function SupplierInvoicesPage() {
  return (
    <div className="space-y-4">
      <PageHeader
        title="Supplier Invoices"
        subtitle={`${SUPPLIER_INVOICES.length} open invoices · ${pesoShort(AP_TOTAL)} payable as of ${AS_OF_LABEL}`}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Aging</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            {AGING_TOTALS.map((b) => (
              <div key={b.bucket}>
                <div className="flex items-center gap-1.5">
                  <span
                    aria-hidden
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ background: AGING_RAMP[b.bucket] }}
                  />
                  <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                    {b.bucket}
                  </p>
                </div>
                <p className="mt-1 text-lg font-semibold tabular-nums">{peso(b.amount)}</p>
              </div>
            ))}
          </div>
          {/* Ordered buckets, so the colour ramps light -> dark with age
              (validated ordinal ramp; 2px surface gaps do the separating). */}
          <div className="mt-4 flex h-2.5 w-full gap-[2px] overflow-hidden rounded-full">
            {AGING_TOTALS.filter((b) => b.amount > 0).map((b, i, arr) => (
              <div
                key={b.bucket}
                title={`${b.bucket}: ${peso(b.amount)}`}
                style={{
                  width: `${(b.amount / AP_TOTAL) * 100}%`,
                  background: AGING_RAMP[b.bucket],
                  borderTopLeftRadius: i === 0 ? 9999 : 0,
                  borderBottomLeftRadius: i === 0 ? 9999 : 0,
                  borderTopRightRadius: i === arr.length - 1 ? 9999 : 0,
                  borderBottomRightRadius: i === arr.length - 1 ? 9999 : 0,
                }}
              />
            ))}
          </div>
          <p className="mt-3 border-t pt-3 text-xs text-muted-foreground">
            <span className="font-medium tabular-nums text-debit">{peso(AP_OVERDUE)}</span> of{' '}
            <span className="font-medium tabular-nums">{peso(AP_TOTAL)}</span> is past due.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Open invoices{' '}
            <span className="text-sm font-normal text-muted-foreground">
              ({SUPPLIER_INVOICES.length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Invoice no.</th>
                  <th className="px-4 py-2">Supplier</th>
                  <th className="px-4 py-2">PO</th>
                  <th className="px-4 py-2">Invoice date</th>
                  <th className="px-4 py-2">Due date</th>
                  <th className="px-4 py-2">Aging</th>
                  <th className="px-4 py-2">Three-way match</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {SUPPLIER_INVOICES.map((inv) => (
                  <tr key={inv.invoiceNo} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-2 font-mono text-xs">{inv.invoiceNo}</td>
                    <td className="px-4 py-2">{inv.supplier}</td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                      {inv.poNo}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{inv.invoiceDate}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{inv.dueDate}</td>
                    <td className="px-4 py-2 text-xs">
                      {inv.bucket === 'Current' ? (
                        <span className="text-muted-foreground">Current</span>
                      ) : (
                        <span className="text-debit">{inv.bucket}</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant={inv.match === 'Matched' ? 'success' : 'warning'}>
                        {inv.match}
                      </Badge>
                      {inv.variance ? (
                        <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                          {peso(inv.variance)} over PO
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums">
                      {peso(inv.amount)}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 font-semibold">
                  <td className="px-4 py-2" colSpan={7}>
                    Total payable
                  </td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums">{peso(AP_TOTAL)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
